/**
 * Reading "is this the installed app?" off the browser - the ONE reader, shared by `InstallPrompt` (the Add to
 * Home Screen card) and the WT1 capture screen (which warns a Safari tab, because Safari can evict photos that
 * have not uploaded; an installed home-screen app is not subject to that). Moved out of `InstallPrompt.tsx`
 * unchanged, so the capture screen reuses this detection rather than growing a second one (WT1 spec section 6).
 * The DECISION stays pure in `installPrompt.ts` (`resolveInstallOffer`); this file only reads.
 */
import type { InstallEnvironment } from './installPrompt';

/**
 * 🚨 DEFENSIVE ON PURPOSE. `InstallPrompt` renders inside Layout, so on every screen. A throw reading
 * `matchMedia` or a Safari private-mode `localStorage` would be a white page app-wide rather
 * than a missing card, which is a far worse failure than the one it is guarding.
 */
export const safely = <T,>(read: () => T, fallback: T): T => {
  try {
    return read();
  } catch {
    return fallback;
  }
};

export const readInstallEnvironment = (deferredEvent: boolean): InstallEnvironment => ({
  userAgent: safely(() => navigator.userAgent, ''),
  displayModeStandalone: safely(
    () => Boolean(window.matchMedia?.('(display-mode: standalone)').matches),
    false
  ),
  // `navigator.standalone` is WebKit-only. Reading it as `undefined` elsewhere is load-bearing:
  // it is half of how an iPad claiming to be a Mac is told from a real Mac.
  iosStandalone: safely(
    () => (navigator as Navigator & { standalone?: boolean }).standalone,
    undefined
  ),
  hasNativeBridge: safely(
    () => Boolean((window as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor),
    false
  ),
  deferredEvent,
  maxTouchPoints: safely(() => navigator.maxTouchPoints, 0),
});
